import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useToastStore } from '@/store';
import Icon from '@/components/Icon';
import useRequireAuth from '@/hooks/useRequireAuth';
import { trackEvent } from '@/constants/utils/analytics';
import { mediumTap } from '@/constants/utils/haptics';

/**
 * Heart control to bookmark a prompt. Anonymous visitors get the standard
 * `AuthCTAModal` (via `useRequireAuth`) instead of a hard sign-in redirect.
 * Stops press propagation so parent cards do not navigate.
 */
function PromptFavoriteHeart({ prompt, hitSlop = 10 }) {
  const { colors } = useTheme();
  const { addToast } = useToastStore(s => s.actions);
  const hasUser = useAuthStore(s => Boolean(s.user?.id));
  const favoritePromptIds = useAuthStore(s => s.user?.favoritePromptIds);
  const toggleFavoritePrompt = useAuthStore(s => s.toggleFavoritePrompt);
  const requireAuth = useRequireAuth();

  const promptId = prompt?.id != null ? String(prompt.id) : '';
  const isFavorited = !!(promptId && (favoritePromptIds || []).map(String).includes(promptId));

  const styles = useMemo(() => StyleSheet.create({
    hit: {
      padding: 6,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
  }), []);

  const onPress = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!promptId) return;
    const ran = requireAuth(
      async () => {
        try {
          const nowFavorited = await toggleFavoritePrompt(promptId);
          mediumTap();
          trackEvent('prompts', nowFavorited ? 'prompts_favorite' : 'prompts_unfavorite', prompt?.slug || promptId);
        } catch (_) {
          addToast('Could not update favorite', 'error');
        }
      },
      {
        source: 'prompts_favorite_heart',
        title: 'Sign in to favorite prompts',
        subtitle: 'Favorites are tied to your account so they follow you across devices.',
        icon: 'heart',
      }
    );
    if (ran === undefined) {
      trackEvent('prompts', 'prompts_favorite_auth_required', prompt?.slug || '');
    }
  }, [promptId, prompt?.slug, requireAuth, toggleFavoritePrompt, addToast]);

  if (!promptId || !hasUser) return null;

  return (
    <Pressable
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ hovered, pressed }) => [
        styles.hit,
        (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
      ]}
      accessibilityRole="button"
      accessibilityLabel={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      accessibilityState={{ selected: isFavorited }}
    >
      <Icon
        name="heart"
        size={15}
        solid={isFavorited}
        color={isFavorited ? '#ef4444' : colors.text_color_tertiary}
      />
    </Pressable>
  );
}

export default React.memo(PromptFavoriteHeart);
